import React, { useCallback, useRef, useState } from "react";

import ElementView from "./ElementView";
import { MIN_SIZE, backgroundCss } from "./constants";

const RAD = Math.PI / 180;

// Rotate a vector from the element's local frame into world space.
const toWorld = (lx, ly, deg) => {
  const c = Math.cos(deg * RAD);
  const s = Math.sin(deg * RAD);
  return { x: lx * c - ly * s, y: lx * s + ly * c };
};

// ...and the inverse: world delta -> the element's rotated frame.
const toLocal = (dx, dy, deg) => {
  const c = Math.cos(deg * RAD);
  const s = Math.sin(deg * RAD);
  return { x: dx * c + dy * s, y: -dx * s + dy * c };
};

function CanvasSurface({
  design,
  elements,
  notesById,
  selectedId,
  editingId,
  editable,
  scale,
  onSelect,
  onStartEdit,
  onEndEdit,
  onPatch,
  onBeforeChange,
  onDropNote,
  onNoteInput,
  onNoteSave,
}) {
  const surfaceRef = useRef(null);
  const gestureRef = useRef(null);
  const [dropHint, setDropHint] = useState(false);

  // Screen coordinates -> canvas units, accounting for zoom.
  const toCanvas = useCallback(
    (clientX, clientY) => {
      const rect = surfaceRef.current.getBoundingClientRect();
      return {
        x: (clientX - rect.left) / scale,
        y: (clientY - rect.top) / scale,
      };
    },
    [scale]
  );

  const handleGestureMove = useCallback(
    (e) => {
      const g = gestureRef.current;
      if (!g) return;

      const dx = (e.clientX - g.startX) / scale;
      const dy = (e.clientY - g.startY) / scale;
      g.moved = true;

      if (g.mode === "move") {
        // Shift locks movement to the dominant axis.
        const lockX = e.shiftKey && Math.abs(dx) >= Math.abs(dy);
        const lockY = e.shiftKey && !lockX;
        onPatch(g.id, {
          x: Math.round(g.el.x + (lockY ? 0 : dx)),
          y: Math.round(g.el.y + (lockX ? 0 : dy)),
        });
        return;
      }

      if (g.mode === "rotate") {
        const rect = surfaceRef.current.getBoundingClientRect();
        const cxScreen = rect.left + (g.el.x + g.el.w / 2) * scale;
        const cyScreen = rect.top + (g.el.y + g.el.h / 2) * scale;
        // +90 because the handle sits above the element, not to its right.
        let deg =
          (Math.atan2(e.clientY - cyScreen, e.clientX - cxScreen) * 180) / Math.PI + 90;
        if (e.shiftKey) deg = Math.round(deg / 15) * 15;
        onPatch(g.id, { rotation: Math.round(deg) });
        return;
      }

      // --- resize ---
      // Work in the element's own rotated frame, then convert the resulting
      // centre shift back to world space so the opposite anchor stays put.
      const { x: lx, y: ly } = toLocal(dx, dy, g.el.rotation || 0);
      const h = g.handle;

      let w = g.el.w;
      let ht = g.el.h;
      let shiftLX = 0;
      let shiftLY = 0;

      if (h.includes("e")) {
        w = Math.max(MIN_SIZE, g.el.w + lx);
        shiftLX = (w - g.el.w) / 2;
      } else if (h.includes("w")) {
        w = Math.max(MIN_SIZE, g.el.w - lx);
        shiftLX = (g.el.w - w) / 2;
      }

      if (h.includes("s")) {
        ht = Math.max(MIN_SIZE, g.el.h + ly);
        shiftLY = (ht - g.el.h) / 2;
      } else if (h.includes("n")) {
        ht = Math.max(MIN_SIZE, g.el.h - ly);
        shiftLY = (g.el.h - ht) / 2;
      }

      // Shift on a corner keeps the original aspect ratio.
      const isCorner = h.length === 2;
      if (e.shiftKey && isCorner && g.el.h > 0) {
        const ratio = g.el.w / g.el.h;
        ht = w / ratio;
        shiftLY = (h.includes("n") ? g.el.h - ht : ht - g.el.h) / 2;
      }

      const shift = toWorld(shiftLX, shiftLY, g.el.rotation || 0);
      const cx = g.el.x + g.el.w / 2 + shift.x;
      const cy = g.el.y + g.el.h / 2 + shift.y;

      onPatch(g.id, {
        w: Math.round(w),
        h: Math.round(ht),
        x: Math.round(cx - w / 2),
        y: Math.round(cy - ht / 2),
      });
    },
    [onPatch, scale]
  );

  const handleGestureEnd = useCallback(() => {
    document.removeEventListener("pointermove", handleGestureMove);
    document.removeEventListener("pointerup", handleGestureEnd);
    gestureRef.current = null;
  }, [handleGestureMove]);

  const startGesture = (e, el, mode, handle) => {
    if (!editable) return;
    // Let the caret land normally while a text element is being edited.
    if (editingId === el.id && mode === "move") return;

    e.preventDefault();
    onSelect(el.id);
    // Snapshot before the first pointermove, so undo restores the element's
    // pre-gesture geometry rather than where the drag happened to end.
    onBeforeChange();

    gestureRef.current = {
      id: el.id,
      el: { ...el },
      mode,
      handle,
      startX: e.clientX,
      startY: e.clientY,
      moved: false,
    };
    document.addEventListener("pointermove", handleGestureMove);
    document.addEventListener("pointerup", handleGestureEnd);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDropHint(false);
    if (!editable) return;

    const noteId = e.dataTransfer.getData("application/x-sarvavya-note");
    if (!noteId) return;

    const { x, y } = toCanvas(e.clientX, e.clientY);
    // Drop so the note is centred under the cursor.
    onDropNote(noteId, Math.round(x - 105), Math.round(y - 100));
  };

  const ordered = [...elements].sort((a, b) => (a.z || 0) - (b.z || 0));

  return (
    <div
      ref={surfaceRef}
      onPointerDown={(e) => {
        // A press on empty canvas clears the selection.
        if (e.target === surfaceRef.current) {
          onSelect(null);
          onEndEdit();
        }
      }}
      onDragOver={(e) => {
        if (!editable) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
        setDropHint(true);
      }}
      onDragLeave={(e) => {
        if (e.target === surfaceRef.current) setDropHint(false);
      }}
      onDrop={handleDrop}
      style={{
        position: "relative",
        width: `${design.width}px`,
        height: `${design.height}px`,
        transform: `scale(${scale})`,
        transformOrigin: "top left",
        overflow: "hidden",
        boxShadow: dropHint
          ? "0 0 0 3px #ff6b35, 0 18px 40px rgba(15,23,42,0.28)"
          : "0 18px 40px rgba(15,23,42,0.28)",
        ...backgroundCss(design.background),
      }}
    >
      {ordered.map((el) => (
        <ElementView
          key={el.id}
          el={el}
          note={el.type === "note" ? notesById[el.noteId] : null}
          selected={selectedId === el.id}
          editing={editingId === el.id}
          editable={editable}
          scale={scale}
          onPointerDown={(e, mode, handle) => startGesture(e, el, mode, handle)}
          onDoubleClick={() => {
            if (!editable || (el.type !== "text" && el.type !== "note")) return;
            // Note text lives on the note, not the element, so it isn't part
            // of canvas history — only snapshot for real canvas edits.
            if (el.type === "text") onBeforeChange();
            onStartEdit(el.id);
          }}
          onTextChange={(text) =>
            el.type === "note"
              ? onNoteInput(el.noteId, text)
              : onPatch(el.id, { text })
          }
          onTextCommit={() => {
            if (el.type === "note") onNoteSave(el.noteId);
            onEndEdit();
          }}
        />
      ))}

      {elements.length === 0 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: "6px",
            color: "#94a3b8",
            fontStyle: "italic",
            pointerEvents: "none",
            textAlign: "center",
            padding: "20px",
          }}
        >
          <span style={{ fontSize: "1.1rem" }}>
            {editable
              ? "Drag a note from the left onto this canvas"
              : "This design is empty."}
          </span>
          {editable && (
            <span style={{ fontSize: "0.9rem" }}>
              …or add text, shapes and images from the toolbar above.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default CanvasSurface;
